import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { API_URL } from './CampaignConstants';

// Safely render any error — FastAPI returns `detail` as either a string OR an
// array of {type, loc, msg, input, url} objects. Toasts must receive a string;
// passing the array crashes React with "Objects are not valid as a React child".
const extractErr = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || 'Request failed';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((x) => x?.msg || JSON.stringify(x)).join('; ');
  }
  try { return JSON.stringify(d); } catch { return 'Request failed'; }
};

// IMPORTANT: this MUST live outside MilestonesEditor. Declaring it inside the
// parent makes every parent re-render mount a brand-new component instance,
// which wipes the form `useState` back to `initial` after every keystroke and
// makes the Save button look broken. See iteration 285 RCA.
const MilestoneForm = ({ initial, busy, onSubmit, onCancel }) => {
  const [f, setF] = useState(initial || { title: '', description: '', progress: 0, status: 'planned' });

  // When the dialog reopens with a fresh `initial`, sync once.
  useEffect(() => {
    if (initial) setF(initial);
  }, [initial]);

  const canSave = (f.title || '').trim().length > 0 && !busy;

  return (
    <div className="space-y-4">
      <div>
        <Label>Title</Label>
        <Input
          value={f.title || ''}
          onChange={(e) => setF({ ...f, title: e.target.value })}
          placeholder="Phase 1 - Foundation"
          data-testid="milestone-title-input"
        />
      </div>
      <div>
        <Label>Description</Label>
        <Textarea
          value={f.description || ''}
          onChange={(e) => setF({ ...f, description: e.target.value })}
          placeholder="Describe this milestone..."
          rows={3}
          data-testid="milestone-description-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Progress (%)</Label>
          <Input
            type="number"
            min={0}
            max={100}
            value={f.progress ?? 0}
            onChange={(e) => setF({ ...f, progress: parseInt(e.target.value) || 0 })}
            data-testid="milestone-progress-input"
          />
        </div>
        <div>
          <Label>Status</Label>
          <Select value={f.status || 'planned'} onValueChange={(v) => setF({ ...f, status: v })}>
            <SelectTrigger data-testid="milestone-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="planned">Planned</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} data-testid="milestone-cancel-btn">Cancel</Button>
        <Button
          onClick={() => onSubmit(f)}
          disabled={!canSave}
          data-testid="milestone-save-btn"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          Save
        </Button>
      </DialogFooter>
    </div>
  );
};

export const MilestonesEditor = ({ campaignId }) => {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null); // the milestone being edited (or null)

  const { data: milestones = [] } = useQuery({
    queryKey: ['campaign-milestones-admin', campaignId],
    queryFn: async () => {
      const res = await api.get(`/campaigns/admin/list`);
      const campaign = res.data.find(c => c.id === campaignId);
      if (!campaign) return [];
      const r = await fetch(`${API_URL}/api/campaigns/${campaign.slug}/milestones`);
      return r.ok ? r.json() : [];
    },
    enabled: !!campaignId,
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const formData = new FormData();
      Object.entries(data).forEach(([k, v]) => formData.append(k, v ?? ''));
      return api.post(`/campaigns/admin/${campaignId}/milestones`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      toast.success('Milestone added');
      queryClient.invalidateQueries({ queryKey: ['campaign-milestones-admin', campaignId] });
      setShowAdd(false);
    },
    onError: (e) => toast.error(extractErr(e)),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const formData = new FormData();
      Object.entries(data).forEach(([k, v]) => formData.append(k, v ?? ''));
      return api.put(`/campaigns/admin/${campaignId}/milestones/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      toast.success('Milestone updated');
      queryClient.invalidateQueries({ queryKey: ['campaign-milestones-admin', campaignId] });
      setEditing(null);
    },
    onError: (e) => toast.error(extractErr(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => api.delete(`/campaigns/admin/${campaignId}/milestones/${id}`),
    onSuccess: () => {
      toast.success('Milestone deleted');
      queryClient.invalidateQueries({ queryKey: ['campaign-milestones-admin', campaignId] });
    },
    onError: (e) => toast.error(extractErr(e)),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Project Milestones</CardTitle>
          <CardDescription>Track progress of project phases</CardDescription>
        </div>
        <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-milestone-btn">
          <Plus className="h-4 w-4 mr-1" />Add Milestone
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {milestones.map((m) => (
            <div key={m.id} className="border rounded-lg p-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium">{m.title}</h4>
                    <Badge variant={m.status === 'completed' ? 'default' : m.status === 'in_progress' ? 'secondary' : 'outline'}>
                      {m.status?.replace('_', ' ')}
                    </Badge>
                  </div>
                  {m.description && <p className="text-sm text-slate-600 mb-2">{m.description}</p>}
                  <Progress value={m.progress} className="h-2" />
                  <p className="text-xs text-slate-500 mt-1">{m.progress}% complete</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(m)} data-testid={`edit-milestone-${m.id}`}>Edit</Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500"
                    onClick={() => { if (window.confirm(`Delete milestone "${m.title}"?`)) deleteMutation.mutate(m.id); }}
                    data-testid={`delete-milestone-${m.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add dialog */}
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent data-testid="add-milestone-dialog">
            <DialogHeader><DialogTitle>Add Milestone</DialogTitle></DialogHeader>
            <MilestoneForm
              initial={{ title: '', description: '', progress: 0, status: 'planned' }}
              busy={createMutation.isPending}
              onSubmit={(data) => createMutation.mutate(data)}
              onCancel={() => setShowAdd(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Edit dialog */}
        <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
          <DialogContent data-testid="edit-milestone-dialog">
            <DialogHeader><DialogTitle>Edit Milestone</DialogTitle></DialogHeader>
            {editing && (
              <MilestoneForm
                initial={editing}
                busy={updateMutation.isPending}
                onSubmit={(data) => updateMutation.mutate({ id: editing.id, data })}
                onCancel={() => setEditing(null)}
              />
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
